import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';

import { IContentTypeValue, IFeature, IOfferEventType } from 'graphql/types';
import { FormSection } from 'common/components/FormSection';
import { ExternalContentSelectorField } from 'external/forms/Field';
import { TOfferEventFormValues } from 'offer/types';
import {
    HEADER_IMAGE_MAX_SIZE,
    HEADER_IMAGE_TYPE,
} from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { useFeature } from 'common/hooks/useFeature';
import {
    ExtraCategoryFormFields,
    SliderField,
    CertificateSelectField,
    SwitchField,
    TextField,
    ImageField,
} from 'common/forms/Field';
import { FormTrainerSelectorField } from 'user/forms/Field';
import {
    CourseSelectorField,
    TrainingSelectorField,
    ScormSelectorField,
} from 'training/forms/Field';
import { VideoSelectorField } from 'video/forms/Field';

interface IProps {
    offerEventId?: string;
    offerEventType: IOfferEventType;
    hideExternalContent?: boolean;
    isOfferDetailDrawer?: boolean;
}

export const OfferEventFormFields = memo(
    ({
        offerEventId,
        offerEventType,
        hideExternalContent,
        isOfferDetailDrawer,
    }: IProps) => {
        const [translate] = useTranslation();

        const { control, watch } = useFormContext<TOfferEventFormValues>();
        const hasDetailPage = watch('hasDetailPage');

        const isNewMeeting = !!useRouteMatch('OFFER_MEETING_CREATE');
        const isNewOffer = !!useRouteMatch('OFFER_CREATE');
        const isOfferDetail =
            !!useRouteMatch('OFFER_DETAIL_DRAWER') || isOfferDetailDrawer;
        const { canUse: canUseScorm, loading: loadingCanUseScorm } = useFeature(
            IFeature.Scorm
        );
        const { canUse: canUseVideo, loading: loadingCanUseVideo } = useFeature(
            IFeature.Video
        );

        const isWebinar = offerEventType === IOfferEventType.Webinar;
        const isMeeting = offerEventType === IOfferEventType.Meeting;
        const isWebinarOrMeeting = isWebinar || isMeeting;
        const isELearning = offerEventType === IOfferEventType.Elearning;

        const isNewMeetingOrOffer = isNewMeeting || isNewOffer;

        return (
            <>
                {(isNewMeetingOrOffer || isOfferDetail) && (
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate('offer.offerExtraFieldsDescription')}
                            </Box>
                        }
                        title={translate('offer.offerExtraFields')}
                    >
                        <ExtraCategoryFormFields
                            contentType={IContentTypeValue.Offer}
                            control={control}
                            name="extraCategories"
                        />
                    </FormSection>
                )}

                {isWebinarOrMeeting && (
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate(
                                    'offer.offerTrainerSectionDescription'
                                )}
                            </Box>
                        }
                        title={translate('trainer')}
                    >
                        <Box sx={{ pt: 2 }}>
                            <FormTrainerSelectorField
                                control={control}
                                label={translate('addTrainer')}
                                name="trainers"
                                selectorQueryVariables={
                                    isNewOffer || !offerEventId
                                        ? undefined
                                        : {
                                              contentType:
                                                  IContentTypeValue.OfferEvent,
                                              objectId: offerEventId,
                                          }
                                }
                            />
                        </Box>
                    </FormSection>
                )}

                {(isWebinarOrMeeting || isELearning) && (
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate(
                                    `offer.${
                                        isELearning
                                            ? 'certificateDescriptionElearning'
                                            : 'certificateDescriptionMeeting'
                                    }`
                                )}
                            </Box>
                        }
                        title={translate('offer.certificateTitle')}
                    >
                        <CertificateSelectField
                            allowExternalCertificates={isWebinarOrMeeting}
                            control={control}
                            name="certificate"
                        />

                        {isELearning && (
                            <SliderField
                                control={control}
                                label={translate('offer.certificateThreshold')}
                                marks={[
                                    { value: 0, label: '0%' },
                                    { value: 1, label: '100%' },
                                ]}
                                max={1}
                                min={0}
                                name="scoreThreshold"
                                step={0.01}
                                valueLabelFormat={(value: number) =>
                                    `${Math.floor(value * 100)}%`
                                }
                            />
                        )}
                    </FormSection>
                )}

                {isWebinarOrMeeting && (
                    <>
                        <FormSection
                            renderExpanded
                            description={
                                <Box>
                                    {translate('offer.trainingsDescription')}
                                </Box>
                            }
                            title={translate('offer.trainingsTitle')}
                        >
                            <Box sx={{ mt: 3 }}>
                                <TrainingSelectorField
                                    control={control}
                                    name="accessTrainings"
                                />
                            </Box>
                        </FormSection>

                        {!hideExternalContent && (
                            <FormSection
                                renderExpanded
                                description={
                                    <Box>
                                        {translate(
                                            'offer.externalContentsDescription'
                                        )}
                                    </Box>
                                }
                                title={translate('offer.externalContentsTitle')}
                            >
                                <Box sx={{ mt: 3 }}>
                                    <ExternalContentSelectorField
                                        control={control}
                                        name="accessExternalContents"
                                    />
                                </Box>
                            </FormSection>
                        )}

                        <FormSection
                            renderExpanded
                            description={
                                <Box>
                                    {translate('offer.coursesDescription')}
                                </Box>
                            }
                            title={translate('offer.coursesTitle')}
                        >
                            <Box sx={{ mt: 3 }}>
                                <CourseSelectorField
                                    control={control}
                                    name="accessCourses"
                                />
                            </Box>
                        </FormSection>

                        {!loadingCanUseScorm && canUseScorm && (
                            <FormSection
                                renderExpanded
                                description={
                                    <Box>
                                        {translate('offer.scormsDescription')}
                                    </Box>
                                }
                                title={translate('offer.scormsTitle')}
                            >
                                <Box sx={{ mt: 3 }}>
                                    <ScormSelectorField
                                        control={control}
                                        name="accessScorms"
                                    />
                                </Box>
                            </FormSection>
                        )}

                        {!loadingCanUseVideo && canUseVideo && (
                            <FormSection
                                renderExpanded
                                description={
                                    <Box>
                                        {translate('offer.videosDescription')}
                                    </Box>
                                }
                                title={translate('offer.videosTitle')}
                            >
                                <Box sx={{ mt: 3 }}>
                                    <VideoSelectorField
                                        control={control}
                                        name="accessVideos"
                                    />
                                </Box>
                            </FormSection>
                        )}
                    </>
                )}

                {isNewMeetingOrOffer && (
                    <FormSection
                        renderExpanded
                        description={
                            <Box>
                                {translate('offer.multipleEventsDescription')}
                            </Box>
                        }
                        title={translate('offer.multipleEvents')}
                    >
                        <SwitchField
                            control={control}
                            formControlProps={{
                                sx: { mb: hasDetailPage ? 2 : 0 },
                            }}
                            label={translate('offer.detailPageEvents')}
                            name="hasDetailPage"
                        />

                        {hasDetailPage && (
                            <Box sx={{ mt: 2 }}>
                                <ImageField
                                    withDefaults
                                    acceptedFileTypes={HEADER_IMAGE_TYPE}
                                    control={control}
                                    helperText={translate(
                                        'imageSizeDescription',
                                        {
                                            width: IMAGE_VARIANTS[
                                                EImageVariant.Header
                                            ].minWidth,
                                            height: IMAGE_VARIANTS[
                                                EImageVariant.Header
                                            ].minHeight,
                                        }
                                    )}
                                    imageVariant={EImageVariant.Header}
                                    label={translate('headerImage')}
                                    maxUploadSize={HEADER_IMAGE_MAX_SIZE}
                                    name="image"
                                    startSlide={0}
                                />

                                {isNewMeetingOrOffer && (
                                    <TextField
                                        multiline
                                        control={control}
                                        label={translate('description')}
                                        name="description"
                                    />
                                )}
                            </Box>
                        )}
                    </FormSection>
                )}
            </>
        );
    }
);
